import { Buffer } from "buffer";
import clsx from "clsx";
import { FC, useEffect } from "react";
import { useFragment, useRelayEnvironment } from "react-relay";
import { useParams } from "react-router-dom";
import { graphql, requestSubscription } from "relay-runtime";
import {
  AssetLabel,
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  nFormatter,
  Toggle,
  useTranslation,
  useWindowSize,
} from "rujira.ui";

import { useIsBelow } from "../../hooks/useBreakpoint";
import { SITE_URL } from "../../home/constants";
import {
  assetToTradeUrlSegment,
  parseUrlSegment,
} from "../../services/assetUrl";
import { useFavorites } from "../../services/favorites";
import { AutomatedTradingPromo } from "./AutomatedTradingPromo";
import { HeaderTradeFragment$key } from "./__generated__/HeaderTradeFragment.graphql";
import { HeaderTradeSummaryFragment$key } from "./__generated__/HeaderTradeSummaryFragment.graphql";
import { TradeBreakpoint } from "../static";
import { useTradeUrlState } from "../tradeUrlState";
import { useOrdersContext } from "./Orders/Context";
import { useRangeContext } from "./Range";
const { AngleDown, Star, TrendUp } = Icons;

const fragment = graphql`
  fragment HeaderTradeFragment on FinPair {
    id
    address
    assetBase {
      asset
      type
      chain
      metadata {
        symbol
        decimals
      }
      price {
        mcap
      }
    }
    assetQuote {
      asset
      type
      chain
      metadata {
        symbol
        decimals
      }
    }
    summary {
      ...HeaderTradeSummaryFragment
    }
  }
`;

const summaryFragment = graphql`
  fragment HeaderTradeSummaryFragment on FinSummary {
    last
    lastUsd
    high
    low
    change
    volumeUsd
  }
`;

const subscription = graphql`
  subscription HeaderTradeSubscription($id: ID!) {
    node(id: $id) {
      ... on FinPair {
        summary {
          ...HeaderTradeSummaryFragment
        }
      }
    }
  }
`;

type UrlAsset = Parameters<typeof assetToTradeUrlSegment>[0];

const analyticsUrl = (base: UrlAsset, quote: UrlAsset) =>
  `${SITE_URL}/analytics/trade/${assetToTradeUrlSegment(base)}/${assetToTradeUrlSegment(quote)}`;

export const Header: FC<{
  k?: HeaderTradeFragment$key;
  lastTradeType?: string;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}> = ({ k, lastTradeType, collapsed, onToggleCollapsed }) => {
  const { t } = useTranslation();
  const data = useFragment(fragment, k);
  const summary = useFragment<HeaderTradeSummaryFragment$key>(
    summaryFragment,
    data?.summary
  );
  const { width } = useWindowSize();
  const min = 470;

  const decimals = 4; // get from base denom
  const change = BigInt(summary?.change || 0);
  const mcap = data?.assetBase.price?.mcap;
  const analyticsHref = data
    ? analyticsUrl(data.assetBase, data.assetQuote)
    : undefined;

  const env = useRelayEnvironment();

  useEffect(() => {
    if (!data?.id) return;
    const sub = requestSubscription(env, {
      subscription,
      variables: {
        id: data?.id,
      },
    });
    return () => {
      sub.dispose();
    };
  }, [data?.id]);
  // Below the trade breakpoint the toggle moves up into the selector row, so
  // the header must not render a second copy of it (or of its tour anchor id).
  const isCondensed = useIsBelow(TradeBreakpoint);

  return (
    <>
      <AutomatedTradingPromo />
      <div
        className={clsx("trade__header-collapsible", {
          "trade__header-collapsible--open": !collapsed,
        })}>
        <div className="trade__header-collapsible-inner">
          <div className="trade__header-stats">
            {!isCondensed && (
              <MarketPrice
                k={k}
                lastTradeType={lastTradeType}
                className="trade__header-price"
              />
            )}
            {!isCondensed && (
              <div className="trade__header-stat">
                <h4>{t("change24h")}</h4>
                <Change change={change} />
              </div>
            )}
            <div className="trade__header-stat">
              <h4>{t("high24h")}</h4>
              <Decimal
                subscript
                amount={BigInt(summary?.high || 0)}
                decimals={12}
                round={width < min ? 4 : decimals}
                symbol={data?.assetQuote.metadata.symbol}
                symbolClassName={"fs-12 color-grey condensed fw-500"}
                clip
              />
            </div>
            <div className="trade__header-stat">
              <h4>{t("low24h")}</h4>
              <Decimal
                subscript
                amount={BigInt(summary?.low || 0)}
                decimals={12}
                round={width < min ? 4 : decimals}
                symbol={data?.assetQuote.metadata.symbol}
                symbolClassName={"fs-12 color-grey condensed fw-500"}
                clip
              />
            </div>
            <div className="trade__header-stat">
              <h4>{t("vol24h")}</h4>
              <div>
                $
                {nFormatter(
                  BigInt(summary?.volumeUsd || 0),
                  width < min ? 2 : 3,
                  8
                )}
              </div>
            </div>
            {!isCondensed && data?.assetBase.price?.mcap ? (
              <div className="trade__header-stat">
                <h4>{t("marketCap")}</h4>
                <div>
                  $
                  {nFormatter(
                    BigInt(data?.assetBase.price?.mcap || 0),
                    width < min ? 2 : 3,
                    0
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {isCondensed && (
            <div className="trade__header-extra">
              {mcap ? (
                <div className="trade__header-extra-mcap">
                  <span className="color-grey">{t("marketCap")}</span>
                  <span>
                    ${nFormatter(BigInt(mcap), width < min ? 2 : 3, 0)}
                  </span>
                </div>
              ) : null}
              {analyticsHref && (
                <a
                  href={analyticsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trade__header-extra-link iflex ai-c no-underline">
                  {t("analytics")}
                  <Icons.External className="w-1.5 h-1.5 ml-0.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      {isCondensed && (
        <button
          type="button"
          className="trade__header-expand"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("showStats") : t("hideStats")}
          onClick={onToggleCollapsed}>
          <Icons.AngleDown className="block w-2" />
        </button>
      )}
      {!isCondensed && analyticsHref && (
        <a
          href={analyticsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="trade__header-stat trade__header-analytics no-underline nowrap">
          <h4 className="iflex ai-c">
            {t("analytics")}
            <Icons.External className="w-1.5 h-1.5 ml-0.5" />
          </h4>
        </a>
      )}

      {!isCondensed && <RangeModeToggle className="ml-a mr-a mr-md-2" />}
      {/* <div className="break" /> */}
    </>
  );
};

const Change: FC<{ change: bigint; className?: string }> = ({
  change,
  className,
}) =>
  change > 0n ? (
    <div className={clsx("flex ai-c color-teal", className)}>
      <span>+{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
      <TrendUp className="block w-2 ml-0.5" />
    </div>
  ) : change < 0n ? (
    <div className={clsx("flex ai-c color-red", className)}>
      <span>{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
      <Icons.TrendDown className="block w-2 ml-0.5" />
    </div>
  ) : (
    <div className={clsx("flex ai-c color-teal", className)}>
      <span>0.00%</span>
    </div>
  );

export const MarketPrice: FC<{
  k?: HeaderTradeFragment$key;
  lastTradeType?: string;
  className?: string;
  condensed?: boolean;
}> = ({ k, lastTradeType, className, condensed }) => {
  const data = useFragment(fragment, k);
  const summary = useFragment<HeaderTradeSummaryFragment$key>(
    summaryFragment,
    data?.summary
  );
  const { width } = useWindowSize();
  const min = 470;
  const decimals = 4; // get from base denom

  const fiat = (
    <Fiat
      amount={BigInt(summary?.lastUsd || 0)}
      decimals={12}
      symbol="$"
      padSymbol={false}
    />
  );

  const last = (
    <Decimal
      amount={BigInt(summary?.last || 0)}
      decimals={12}
      round={width < min ? 4 : decimals}
      as="h2"
      clip
      className={
        lastTradeType === "sell"
          ? "color-red"
          : lastTradeType === "buy"
            ? "color-teal"
            : ""
      }
      symbol={data?.assetQuote.metadata.symbol}
      symbolClassName={"fs-12 color-grey condensed fw-500"}
    />
  );

  if (!condensed)
    return (
      <div className={className}>
        {last}
        {fiat}
      </div>
    );

  return (
    <div className={className}>
      <div className="trade__selector-price-top">{last}</div>
      <div className="trade__selector-price-bottom">
        {fiat}
        <Change change={BigInt(summary?.change || 0)} className="ml-1" />
      </div>
    </div>
  );
};

export const RangeModeToggle: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const { enabled } = useRangeContext();
  const { selectPreviousTab } = useOrdersContext();
  const [, setTradeUrlState] = useTradeUrlState();

  return (
    <div
      className={clsx("trade__header-range flex ai-c", className)}
      id="trade-header-toggle">
      <span
        onClick={enabled ? selectPreviousTab : undefined}
        className={`fs-12 fs-sm-14 condensed fw-500 ${enabled ? "pointer color-grey" : "color-white"} block mr-0.5 mr-sm-1`}>
        {t("manual")}
      </span>
      <Toggle
        className={`trade__header-range-toggle toggle--sm ${enabled ? "color-white" : "color-grey"}`}
        label={t("automated")}
        checked={enabled}
        onChange={(e) =>
          e.target.checked
            ? setTradeUrlState({ type: "automated", strategy: "ccl" })
            : selectPreviousTab()
        }
      />
    </div>
  );
};

export const MarketSelect: FC<{
  k?: HeaderTradeFragment$key;
  openDrawer: () => void;
  initialPairId?: string;
}> = ({ k, openDrawer, initialPairId }) => {
  const params = useParams();
  const data = useFragment(fragment, k);
  const { favorites, addFavorite, removeFavorite } = useFavorites();

  const pairId = data?.address
    ? Buffer.from(`FinPair:${data.address}`).toString("base64")
    : initialPairId;
  const isFavorite = !!pairId && favorites.includes(pairId);

  const starClick = () => {
    if (!pairId) return;
    if (isFavorite) {
      removeFavorite(pairId);
    } else {
      addFavorite(pairId);
    }
  };

  const base = params.base?.split(".")[0];
  const quote = params.quote?.split(".")[0];
  const baseChain = params.base?.split(".")[1] || "";
  const quoteChain = params.quote?.split(".")[1] || "";

  const baseAsset = data?.assetBase || {
    ...parseUrlSegment(params.base || ""),
    type: baseChain !== "" ? "SECURED" : "LAYER_1",
  };
  const quoteAsset = data?.assetQuote || {
    ...parseUrlSegment(params.quote || ""),
    type: quoteChain !== "" ? "SECURED" : "LAYER_1",
  };

  return (
    <div className="trade__selector-market flex condensed ai-c gap-0.5">
      {isFavorite ? (
        <Icons.StarSolid
          className="trade__header-favorite trade__header-favorite--solid pointer"
          onClick={starClick}
        />
      ) : (
        <Star className="trade__header-favorite pointer" onClick={starClick} />
      )}
      <button
        className="trade__selector-pair"
        onClick={() => {
          openDrawer();
        }}>
        <IconDenom
          denom={data?.assetBase.metadata.symbol || base || ""}
          className="denom"
        />
        <IconDenom
          denom={data?.assetQuote.metadata.symbol || quote || ""}
          className="denom"
        />
        <h1 className="mr-1">
          <AssetLabel
            asset={baseAsset}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
          <span>/</span>{" "}
          <AssetLabel
            asset={quoteAsset}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
        </h1>
        <AngleDown />
      </button>
    </div>
  );
};

export const MarketSummary: FC<{
  k?: HeaderTradeFragment$key;
}> = ({ k }) => {
  const { t } = useTranslation();
  const data = useFragment(fragment, k);
  const summary = useFragment<HeaderTradeSummaryFragment$key>(
    summaryFragment,
    data?.summary
  );

  const { width } = useWindowSize();
  const min = 470;

  const decimals = 4; // get from base denom
  const change = BigInt(summary?.change || 0);

  const env = useRelayEnvironment();

  useEffect(() => {
    if (!data?.id) return;
    const sub = requestSubscription(env, {
      subscription,
      variables: {
        id: data?.id,
      },
    });
    return () => {
      sub.dispose();
    };
  }, [data?.id]);

  return (
    <div className="trade__header">
      <div className="trade__header-price">
        <Decimal
          amount={BigInt(summary?.last || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
          as="h2"
        />
        <Fiat
          amount={BigInt(summary?.lastUsd || 0)}
          decimals={12}
          symbol="$"
          padSymbol={false}
        />
      </div>
      <div className="trade__header-stat">
        <h4>{t("change24h")}</h4>
        {change > 0n ? (
          <div className="flex ai-c color-teal">
            <span>+{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <TrendUp className="block w-2 ml-0.5" />
          </div>
        ) : change < 0n ? (
          <div className="flex ai-c color-red">
            <span>{(Number(change) / 10 ** 10).toLocaleDecimal(2)}%</span>
            <Icons.TrendDown className="block w-2 ml-0.5" />
          </div>
        ) : (
          <div className="flex ai-c color-teal">
            <span>0.00%</span>
          </div>
        )}
      </div>
      <div className="trade__header-stat">
        <h4>{t("high24h")}</h4>
        <Decimal
          amount={BigInt(summary?.high || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
        />
      </div>
      <div className="trade__header-stat">
        <h4>{t("low24h")}</h4>
        <Decimal
          amount={BigInt(summary?.low || 0)}
          decimals={12}
          round={width < min ? 4 : decimals}
        />
      </div>
      <div className="trade__header-stat">
        <h4>{t("vol24h")}</h4>
        <div>
          ${nFormatter(BigInt(summary?.volumeUsd || 0), width < min ? 2 : 3, 8)}
        </div>
      </div>
      {data?.assetBase.price?.mcap ? (
        <div className="trade__header-stat">
          <h4>{t("marketCap")}</h4>
          <div>
            $
            {nFormatter(
              BigInt(data?.assetBase.price?.mcap || 0),
              width < min ? 2 : 3,
              0
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const MarketPriceFallback: FC<{ className?: string }> = ({
  className,
}) => (
  <div className={className}>
    <div className="trade__selector-price-top">
      <h2>
        <span className="inline-block skeleton w-16 h-3 br-2" />
      </h2>
    </div>
    <div className="trade__selector-price-bottom">
      <Fiat amount={0n} symbol="$" padSymbol={false} />
      <span className="inline-block skeleton w-6 h-1.5 br-2 ml-1" />
    </div>
  </div>
);

export const MarketSummaryFallback = () => {
  const { t } = useTranslation();
  const isCondensed = useIsBelow(TradeBreakpoint);

  if (isCondensed)
    return (
      <div className="trade__header-expand" aria-hidden="true">
        <Icons.AngleDown className="block w-2" />
      </div>
    );

  return (
    <div className="trade__header-stats">
      <div className="trade__header-price">
        <h2>{t("loading")}</h2>
        <Fiat amount={0n} symbol="$" padSymbol={false} />
      </div>
      <div className="trade__header-stat">
        <h4>{t("change24h")}</h4>
        <span className="inline-block skeleton w-8 h-2 br-2" />
      </div>
      <div className="trade__header-stat">
        <h4>{t("high24h")}</h4>
        <span className="inline-block skeleton w-8 h-2 br-2" />
      </div>
      <div className="trade__header-stat">
        <h4>{t("low24h")}</h4>
        <span className="inline-block skeleton w-8 h-2 br-2" />
      </div>
      <div className="trade__header-stat">
        <h4>{t("vol24h")}</h4>
        <span className="inline-block skeleton w-8 h-2 br-2" />
      </div>
    </div>
  );
};
